import type { DateKey, MonthKey } from './dates.js';

/** Monday Booking (`status14`) label ids – filter on the id, not the text (mapping doc 2026-10-06). */
export const BOOKING = {
  CANCELLED: 0,
  BOOKED: 1,
  NOT_BOOKED: 2,
  RESCHEDULE: 3,
  PRELIMINARY: 4,
  NOT_STARTED: 5,
  PAUSED: 6,
  FAKTURERA: 7
} as const;

/** FLOW-1046 (4a): only Booked and Preliminary count. */
export const COUNTED_BOOKING: readonly number[] = [BOOKING.BOOKED, BOOKING.PRELIMINARY];

export const EDITING_FINISHED = 'Finished';
export const EDITING_X_DRAFT_SENT = 'X draft, Sent to client';
export const EDITING_FEEDBACK_RECEIVED = 'Feedback received';
export const EDITING_DIRECTOR_FEEDBACK = 'Director feedback';

/** A `personsAndTeams` entry of a Monday people column. */
export interface MondayPersonRef {
  id: string;
  kind: 'person' | 'team';
}

/** One project of Video Management (1240882153) as mirrored (`legacy.monday_mirror_item`). */
export interface MirrorItem {
  id: string;
  name: string;
  /** Month parsed from the group title ("October - 2026" → 2026-10); null when the title is not a month group. */
  groupMonth: MonthKey | null;
  groupTitle?: string | null;
  /** Booking `status14`: label id and text. */
  status: { index: number | null; label: string };
  /** Package `status82` label; '' when blank. */
  packageLabel: string;
  videoCount?: number | null;
  /** Editor `people2`. */
  editors: MondayPersonRef[];
  /** Camera Person `people7`. */
  photographers: MondayPersonRef[];
  /** Shoot Date `date4`. */
  shootDate: DateKey | null;
  /** Editing timeline `timeline`. */
  editTimeline: { from: DateKey; to: DateKey } | null;
  /** Internal deadline `date8`. */
  internalDeadline: DateKey | null;
  /** 1st draft due date `date42`. */
  firstDraft: DateKey | null;
  /** X draft due date `date_1`. */
  nextDraft: DateKey | null;
  /** Scheduled time `siffror9__1` (planned time on site); Time on Set `siffror__1` is never used. */
  scheduledOnSiteH: number | null;
  /** Editing Status `dup__of_shooting_status_` label. */
  editingStatus: string | null;
  /** For the project import (Del 6 §6.3.2): All Client Data link `connect_boards64`, Creative Director `person`,
   * Producer `people`, Shooting Status `status_1`. Optional so older mirror rows and fixtures still read. */
  client?: { itemId: string; name: string } | null;
  cds?: MondayPersonRef[];
  producers?: MondayPersonRef[];
  shootingStatus?: string | null;
  /** Folder and review links for the project's Files tab: External Drive folder `text2`, Internal Drive folder
   * `text_mm3skn1k`, Ziflow review folder `link_mm2wyzn5`, Videobrief `link_mm4189cs`. https URLs only. */
  driveExternalUrl?: string | null;
  driveInternalUrl?: string | null;
  ziflowFolderUrl?: string | null;
  briefUrl?: string | null;
  /** The "Ziflow" column `color_mm0ep2fq`: where the project is reviewed (owner 2026-10-24: Ziflow and the Hub side by
   * side until 9 November). Null when the label is blank or one the Hub does not know. */
  reviewTool?: ReviewTool | null;
  /** The delivery-spec columns' labels as Monday shows them (Delivery, Advertising, Platforms, …), read into a project's
   * spec by the import (@kff/domain specFromMonday, owner 2026-11-01). Keys as MondaySpecRaw. */
  specRaw?: Record<string, string | null> | null;
}

export type ReviewTool = 'ziflow' | 'hub' | 'drive';

/** `legacy.monday_mirror_feedback`: the day Editing Status became "Feedback received" for a round. */
export interface FeedbackRound {
  itemId: string;
  round: number;
  receivedOn: DateKey;
  /** A5 / h5: the "Feedback received" was changed to Director feedback without a new "Sent to client". */
  changedToDirector?: boolean;
  source?: 'observed' | 'activity_log' | 'derived';
}

/** A row of the consultant list (18409940404). */
export interface ConsultantRow {
  /** Monday item id of the row – used as the person key in workload output. */
  rowId: string;
  name: string;
  /** `text_mm7hysm8` – one or more Monday user ids. Empty = cannot be matched (h8). */
  mondayUserIds: string[];
  email: string | null;
  /** In the group *Active Freelancers* (`topics`). */
  active: boolean;
  /** Availability `color_mm2p61xv` = Terminated. */
  terminated: boolean;
  /** Linked Hub person (by email), if any. */
  personId?: string | null;
}

/** Monday user (for names in "Not counted" and matching rows without Monday id). */
export interface MondayUser {
  id: string;
  name: string;
  email?: string | null;
  /** the user's mobile (or phone) in Monday, seeds the Hub profile */
  phone?: string | null;
}

/** Hours Matrix (Editor) 18411143355 row. */
export interface PackageHours {
  name: string;
  /** First Draft Hours `numbers__1` – per project. */
  firstDraftH: number;
  /** Avg Drafts (H) `numbers_mkkg6w57` – hours PER revision round (owner answer 1, 2026-10-06). */
  revisionH: number;
  minDays?: number | null;
}

/** Hours Matrix (Camera Person) 18411143421 row. */
export interface PhotographerTime {
  name: string;
  onSiteH: number;
  prepH: number;
  travelH: number;
}

export interface Absence {
  from: DateKey;
  to: DateKey;
  part: 'full' | 'am' | 'pm';
}

/** Per person inputs to placement: weekend availability per day and absences (Personio / Hub). */
export interface PersonPlacementInput {
  /** Weekend day → hours the person can work (0 < h ≤ 8). Days not listed are not usable. */
  weekendHours?: Record<DateKey, number>;
  absences?: Absence[];
}

export interface EngineConfig {
  /** `team.day_max_h` */
  dayMaxH: number;
  /** `team.feedback_bd` */
  feedbackBd: number;
  /** Included feedback rounds (rounds after these are "not marked as booked"). */
  includedRounds: number;
  /** Package used for blank / INCLUDED / unknown labels. */
  fallbackPackage: string;
  /** Safety limit for revision rounds per project. */
  maxRounds: number;
}

export const DEFAULT_CONFIG: EngineConfig = {
  dayMaxH: 8,
  feedbackBd: 3,
  includedRounds: 2,
  fallbackPackage: 'Bergman',
  maxRounds: 20
};

export interface EngineInput {
  /** Europe/Stockholm date the computation is for. */
  today: DateKey;
  items: MirrorItem[];
  feedback: FeedbackRound[];
  consultants: ConsultantRow[];
  users?: MondayUser[];
  packages: PackageHours[];
  photographerTime: PhotographerTime[];
  /** Keyed by consultant rowId. */
  people?: Record<string, PersonPlacementInput>;
  config?: Partial<EngineConfig>;
  /** Extra closed days (KFF stängt). */
  closedDays?: DateKey[];
}

export type RuleKey =
  | 'timeline'
  | 'internal'
  | 'first_draft'
  | 'internal_fwd'
  | 'first_draft_fwd'
  | 'after_shoot'
  | 'after_feedback'
  | 'shoot_day';

export type LineKind = 'edit' | 'rev' | 'shoot';

export interface WorkloadLine {
  projectId: string;
  projectName: string;
  personKey: string;
  kind: LineKind;
  round?: number;
  feedbackOn?: DateKey;
  hours: number;
  /** Day → hours. */
  days: Record<DateKey, number>;
  rule: RuleKey;
  /** Package row used. */
  packageUsed: string;
  /** "Spielberg" or "Package missing – counted as Bergman (8 h)". */
  packageNote: string;
  /** Number of people in the Editor column sharing the hours. */
  split: number;
  statusLabel: string;
  /** Round after the included ones – "not marked as booked". */
  unbooked?: boolean;
  /** Hours that did not fit on the editing timeline (h2). */
  afterTimelineH?: number;
  /** h3 flag text. */
  flag?: string;
  /** h11: read from an older month group. */
  oldGroup?: string;
  onSite?: { source: 'project' | 'package'; onSiteH: number; prepH: number; travelH: number };
  /** Weekend shoot: shown on the day but outside capacity – never counted in load % or overtime. */
  outsideCapacity?: boolean;
  /** Human readable reason ("Where the hours come from"). */
  reason: string;
}

export interface WaitingMarker {
  projectId: string;
  projectName: string;
  personKey: string;
  round: number;
  draftOn: DateKey;
  expectedBy: DateKey;
  /** Hours the round gets once the feedback is in (never counted before). */
  hours: number;
  unbooked: boolean;
  statusLabel: string;
  reason: string;
}

export interface UndatedEntry {
  projectId: string;
  projectName: string;
  month: MonthKey | null;
  statusLabel: string;
  packageNote: string;
  personKeys: string[];
  hours: number;
  shootOnly: boolean;
  pastMonth: boolean;
  noPeople: boolean;
}

export interface ExcludedEntry {
  projectId: string;
  projectName: string;
  why: string;
}

export type NotCountedWhy = 'not_on_list' | 'no_monday_id' | 'team';

export interface NotCountedShare {
  projectId: string;
  projectName: string;
  mondayUserId: string;
  personName: string;
  role: 'editor' | 'photographer';
  why: NotCountedWhy;
  hours: number;
  text: string;
}

export interface CheckFlag {
  projectId: string;
  projectName: string;
  kind: 'dates_before_shoot' | 'director_feedback';
  on?: DateKey;
  text: string;
}

export interface ConsultantSuggestion {
  /** Stable key used by `suggestion_dismissal`. */
  key: string;
  mondayUserId: string;
  personName: string;
  why: 'not_on_list' | 'no_monday_id';
  projectIds: string[];
  hours: number;
  text: string;
}

export interface PersonWeek {
  /** Counted demand (excludes outside-capacity weekend shoots). */
  demandH: number;
  /** Weekend shoot hours shown but outside capacity. */
  outsideCapacityH: number;
  /** Part of demand from rounds after the included ones. */
  unbookedH: number;
  /** Counted hours per day. */
  days: Record<DateKey, number>;
  /** Outside-capacity hours per day. */
  outsideDays: Record<DateKey, number>;
}

export interface WorkloadScope {
  groups: MonthKey[];
  from: DateKey;
  to: DateKey;
}

export interface WorkloadResult {
  scope: WorkloadScope;
  lines: WorkloadLine[];
  waiting: WaitingMarker[];
  undated: UndatedEntry[];
  excluded: ExcludedEntry[];
  notCounted: NotCountedShare[];
  notCountedH: number;
  flags: CheckFlag[];
  /** Items in older groups without a date in the window (never read by the adapter). */
  unread: string[];
  suggestions: ConsultantSuggestion[];
  /** personKey → week Monday → totals. */
  byPersonWeek: Record<string, Record<DateKey, PersonWeek>>;
  /** consultant rowId → display name */
  personNames: Record<string, string>;
}
